import React, { useContext } from 'react';

import { useSelector } from 'react-redux';

import { ApiCommentOrPlaceholder } from 'src/components/conversation-provider/types';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { useIntl } from 'src/hooks/intl';
import {
  getPullRequestDestinationHash,
  getPullRequestSourceHash,
} from 'src/redux/pull-request/selectors';
import { commentPermalink } from 'src/redux/pull-request/utils/comments';
import { Link } from 'src/router/components';
import { useParams } from 'src/router/hooks';
import urls from 'src/urls';

import {
  DiffStateContext,
  SelectedDiffType,
} from '../tabs/diff/diff-state-context';

import { usePublishActivityUiEvent } from './hooks';
import messages from './pull-request-comment-title.i18n';

export function PullRequestCommentTitle(props: {
  latestComment: ApiCommentOrPlaceholder;
}): JSX.Element {
  const { activityFeedVersion } = useConversationsContext();
  const publish = usePublishActivityUiEvent(
    'file-comment',
    activityFeedVersion || ''
  );
  const intl = useIntl();
  const prAnchorHash = useSelector(getPullRequestSourceHash) || '';
  const prDestinationHash = useSelector(getPullRequestDestinationHash) || '';
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  let renderedComment;

  const { setIterativeReviewSelectedDiff } = useContext(DiffStateContext);

  // Extract the file path from the top comment
  const filePath = props.latestComment.inline?.path;
  const srcRev = props.latestComment.inline?.src_rev;
  const baseRev = props.latestComment.inline?.base_rev;

  // Check if the comment is on a specific file in the diff
  if (filePath) {
    // Extract the file name from the file path
    const fileNameFromPath = filePath.split('/').pop();

    // Construct diff link using the permalink of the last comment
    const diffTabRedirect = urls.ui.pullRequestTab(
      repositoryOwner,
      repositorySlug,
      pullRequestId,
      'diff'
    );
    const diffRange = baseRev ? `/${srcRev}..${baseRev}` : '';
    const diffLinkForComment = `${diffTabRedirect}${diffRange}#${commentPermalink(
      props.latestComment
    )}`;

    const onClick = () => {
      publish();
      if (diffRange) {
        setIterativeReviewSelectedDiff({
          sourceHash: srcRev!,
          destHash: baseRev,
          selectedDiffType: SelectedDiffType.Custom,
          isLoading: false,
        });
      } else {
        setIterativeReviewSelectedDiff({
          sourceHash: prAnchorHash,
          destHash: prDestinationHash,
          selectedDiffType: SelectedDiffType.All,
          isLoading: false,
        });
      }
    };

    // Construct comment component for file-specific comments
    renderedComment = (
      <>
        {intl.formatMessage(messages.commentOnDiffTitle)}{' '}
        <Link to={diffLinkForComment} onClick={onClick}>
          {fileNameFromPath}
        </Link>
      </>
    );
  } else {
    // Construct comment component for comments on PR overview
    renderedComment = <>{intl.formatMessage(messages.commentOnOverview)}</>;
  }

  return renderedComment;
}
